import { useState } from 'react';
import { clsx } from 'clsx';
import type { CharacterReadiness, ReadinessItem } from '../../hooks/useCharacterReadiness';
import { useCharacterCreationStore } from '../../stores/characterCreationStore';
import { CheckIcon, ChevronUpIcon } from '../ui/Icons';

interface ReadinessChecklistProps {
  readiness: CharacterReadiness;
}

export function ReadinessChecklist({ readiness }: ReadinessChecklistProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const { setDockTab, setIdentitySubTab } = useCharacterCreationStore();
  const { items, requiredComplete, requiredTotal, isReady } = readiness;

  const handleNavigate = (item: ReadinessItem) => {
    setDockTab(item.dockTab);
    if (item.subTab) {
      setIdentitySubTab(item.subTab);
    }
  };

  return (
    <div className="border-b border-border-default">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-background-elevated/50 transition-colors"
      >
        <span className="text-sm font-semibold text-text-primary">
          Character Readiness
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted">
            {requiredComplete}/{requiredTotal}
          </span>
          <ChevronUpIcon
            className={clsx(
              'w-4 h-4 text-text-muted transition-transform',
              !isExpanded && 'rotate-180'
            )}
          />
        </div>
      </button>

      {/* Checklist items */}
      {isExpanded && (
        <div className="px-4 pb-3 space-y-1">
          {items.map((item) => (
            <ChecklistRow
              key={item.id}
              item={item}
              onNavigate={() => handleNavigate(item)}
            />
          ))}

          {/* Ready banner */}
          {isReady && (
            <div className="mt-2 px-3 py-2 rounded-lg bg-success/10 border border-success/20 text-center">
              <span className="text-sm font-medium text-success flex items-center justify-center gap-1.5">
                <CheckIcon className="w-4 h-4" />
                Ready to Play!
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChecklistRow({ item, onNavigate }: { item: ReadinessItem; onNavigate: () => void }) {
  if (item.complete) {
    return (
      <div className="flex items-center gap-2 py-1">
        <CheckIcon className="w-4 h-4 text-success flex-shrink-0" />
        <span className="text-sm text-text-muted">{item.label}</span>
        <span className="text-xs text-text-muted ml-auto">Done</span>
      </div>
    );
  }

  if (!item.required) {
    return (
      <button
        type="button"
        onClick={onNavigate}
        className="w-full flex items-center gap-2 py-1 group"
      >
        <div className="w-4 h-4 rounded-full border border-border-default flex-shrink-0" />
        <span className="text-sm text-text-secondary group-hover:text-text-primary transition-colors">
          {item.label}
        </span>
        <span className="text-xs text-text-muted ml-auto">Optional</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onNavigate}
      className="w-full flex items-center gap-2 py-1 group"
    >
      <div className="w-4 h-4 rounded-full border border-danger/50 flex-shrink-0" />
      <span className="text-sm text-accent-gold group-hover:text-accent-gold/80 transition-colors">
        {item.label}
      </span>
    </button>
  );
}
